import { dateOfBirth } from './utils';

const CBC_RANGES = [
  { test: 'WBC', low: '3.53',  high: '9.52',  units: 'x10\u00B3/uL' },
  { test: 'LY',  low: '20.23', high: '43.53', units: '%' },
  { test: 'MO',  low: '5.23',  high: '13.22', units: '%' },
  { test: 'NE',  low: '40.62', high: '70.51', units: '%' },
  { test: 'EO',  low: '0.84',  high: '7.67',  units: '%' },
];

const BLANK_ROWS = Array(10).fill({ test: '', low: '', high: '', units: '' });

const TEMPLATES = {
  cbc: {
    title: 'CBC Test Results',
    machineName: 'CBC machine',
    showLowHigh: true,
    tableRows: CBC_RANGES,
    refRanges: CBC_RANGES,
  },
  chemistry: {
    title: 'Chemistry Test Results',
    machineName: 'Piccolo',
    showLowHigh: true,
    tableRows: [],
    refRanges: BLANK_ROWS,
  },
  hormone: {
    title: 'Hormone Tests',
    machineName: 'Tosoh machine',
    showLowHigh: false,
    tableRows: [],
    refRanges: BLANK_ROWS,
  },
  blank: null, // handled separately — simple cover sheet with paste area
};

export const printLabTemplate = (templateType, currentPatient, orderingClinician) => {
  if (!(templateType in TEMPLATES)) return;

  const logoUrl = `${window.location.origin}/img/HH_for_Circle.png`;

  // ── Blank cover-sheet template ────────────────────────────────────────────
  if (templateType === 'blank') {
    const patientName = currentPatient
      ? `${currentPatient.last_name || ''}, ${currentPatient.first_name || ''}`
      : '';
    const patientId = currentPatient?.ris_patients_id || '';
    const dob = currentPatient?.dob ? dateOfBirth(currentPatient.dob) : '';
    const clinician = orderingClinician || '';

    const blankHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Health Hub Laboratory</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      color: #000;
      background: #fff;
      padding: 36px 48px 80px;
    }
    .header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
    }
    .header-logo-col { flex: 0 0 110px; display: flex; align-items: center; }
    .header-logo { width: 110px; height: 110px; object-fit: contain; }
    .header-text { flex: 1; text-align: center; }
    .header-text h1 { font-size: 26pt; font-weight: bold; margin-bottom: 4px; }
    .header-text h2 { font-size: 16pt; font-weight: bold; margin-bottom: 3px; }
    .header-text p { font-size: 10pt; line-height: 1.5; }
    .header-spacer { flex: 0 0 110px; }
    .divider { border: none; border-top: 1.5px solid #000; margin: 0 0 16px; }
    .patient-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      margin-bottom: 24px;
    }
    .patient-box {
      border: 1.5px solid #000;
      border-radius: 8px;
      padding: 10px 16px;
      width: 52%;
    }
    .collected-box {
      border: 1.5px solid #000;
      border-radius: 8px;
      padding: 10px 16px;
      width: 38%;
    }
    .patient-box p, .collected-box p { font-size: 11pt; line-height: 1.9; }
    .paste-area {
      border: 1.5px dashed #999;
      border-radius: 6px;
      min-height: 420px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .paste-area-hint { font-size: 10pt; color: #ccc; font-style: italic; }
    .footer {
      position: fixed;
      bottom: 0; left: 0; right: 0;
      padding: 8px 48px;
      border-top: 1px solid #000;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      font-size: 9pt;
      background: #fff;
    }
    .footer-center { text-align: center; }
    .footer-right  { text-align: right; }
    @media print {
      body { padding: 20px 36px 72px; }
      .footer { bottom: 6px; }
      .paste-area { min-height: 460px; }
      .header-logo { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <div class="header">
    <div class="header-logo-col">
      <img class="header-logo" src="${logoUrl}" alt="Health Hub Logo" />
    </div>
    <div class="header-text">
      <h1>Results</h1>
      <h2>Health Hub Laboratory</h2>
      <p>Phone: (417) 660-4469</p>
      <p>Fax: (417) 281-0061</p>
    </div>
    <div class="header-spacer"></div>
  </div>

  <hr class="divider" />

  <div class="patient-row">
    <div class="patient-box">
      <p>Name: ${patientName}</p>
      <p>Patient ID: ${patientId}</p>
      <p>DOB: ${dob}</p>
      <p>Ordering Clinician:&nbsp; ${clinician}</p>
    </div>
    <div class="collected-box">
      <p><strong>Collected Date:</strong></p>
      <p><strong>Collected Time:</strong></p>
    </div>
  </div>

  <div class="paste-area">
    <span class="paste-area-hint">Affix lab results here</span>
  </div>

  <div class="footer">
    <div><p>Health Hub Clinic</p></div>
    <div class="footer-center"><p>healthhubclinic.com</p></div>
    <div class="footer-right">
      <p>(417) 660-4469</p>
      <p style="font-size:8pt">Unauthorized use is strictly prohibited &mdash; contact immediately if this material is received in error.</p>
    </div>
  </div>

</body>
</html>`;

    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) {
      alert('Pop-up blocked. Please allow pop-ups for this site to print templates.');
      return;
    }
    win.document.write(blankHtml);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
    return;
  }

  // ── Structured templates (CBC / Chemistry / Hormone) ─────────────────────
  const tmpl = TEMPLATES[templateType];

  const patientName = currentPatient
    ? `${currentPatient.last_name || ''}, ${currentPatient.first_name || ''}`
    : '';
  const patientId = currentPatient?.ris_patients_id || '';
  const dob = currentPatient?.dob ? dateOfBirth(currentPatient.dob) : '';
  const clinician = orderingClinician || '';

  const refColHeaders = tmpl.showLowHigh
    ? '<th style="width:12%">Low</th><th style="width:12%">High</th>'
    : '<th colspan="2" style="width:24%">Reference Range</th>';

  const tableBodyRows = tmpl.tableRows.map(r => `
    <tr>
      <td><strong>${r.test}</strong></td>
      <td></td>
      <td></td>
      <td>${r.low}</td>
      <td>${r.high}</td>
      <td>${r.units}</td>
    </tr>`).join('');

  const refTableRows = tmpl.refRanges.map(r => `
    <tr>
      <td style="height:22px">${r.test || ''}</td>
      <td>${r.low || ''}</td>
      <td>${r.high || ''}</td>
      <td>${r.units || ''}</td>
    </tr>`).join('');

  const refColHeadersSimple = tmpl.showLowHigh
    ? '<th>Low</th><th>High</th>'
    : '<th colspan="2">Reference Range</th>';

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${tmpl.title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 12pt;
      color: #000;
      background: #fff;
      padding: 36px 48px 80px;
    }

    /* ── Header ── */
    .header {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
    }
    .header-logo-col {
      flex: 0 0 110px;
      display: flex;
      align-items: center;
    }
    .header-logo {
      width: 110px;
      height: 110px;
      object-fit: contain;
    }
    .header-text {
      flex: 1;
      text-align: center;
    }
    .header-text h1 {
      font-size: 26pt;
      font-weight: bold;
      margin-bottom: 4px;
    }
    .header-text h2 {
      font-size: 16pt;
      font-weight: bold;
      margin-bottom: 3px;
    }
    .header-text p {
      font-size: 10pt;
      line-height: 1.5;
    }
    .header-spacer {
      flex: 0 0 110px;
    }

    /* ── Banner ── */
    .banner {
      border: 1.5px solid #000;
      border-radius: 4px;
      text-align: center;
      padding: 5px 12px;
      margin: 10px 0 18px;
    }
    .banner h3 { font-size: 13pt; font-weight: bold; }

    /* ── Patient / Collected row ── */
    .patient-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      margin-bottom: 20px;
    }
    .patient-box {
      border: 1.5px solid #000;
      border-radius: 8px;
      padding: 10px 16px;
      width: 52%;
    }
    .collected-box {
      border: 1.5px solid #000;
      border-radius: 8px;
      padding: 10px 16px;
      width: 38%;
    }
    .patient-box p, .collected-box p {
      font-size: 11pt;
      line-height: 1.9;
    }

    /* ── Results table ── */
    .results-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 10px;
    }
    .results-table th {
      font-size: 11pt;
      font-weight: bold;
      text-align: left;
      padding: 5px 8px;
      border-bottom: 1.5px solid #000;
    }
    .results-table .subrow th {
      font-size: 10.5pt;
      font-weight: bold;
      padding: 3px 8px;
      border-bottom: 1px solid #000;
    }
    .results-table td {
      font-size: 11pt;
      padding: 4px 8px;
      border-bottom: 1px solid #ddd;
    }
    .results-table tr:last-child td { border-bottom: none; }

    /* ── Machine label ── */
    .machine-label {
      font-size: 11pt;
      margin: 8px 0 6px;
    }

    /* ── Paste area ── */
    .paste-area {
      border: 2px dashed #aaa;
      min-height: 200px;
      margin: 10px 0 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .paste-area-hint {
      font-size: 10pt;
      color: #bbb;
      font-style: italic;
    }

    /* ── Reference ranges section ── */
    .ref-section {
      margin-top: 18px;
      page-break-inside: avoid;
    }
    .ref-section h4 {
      font-size: 11pt;
      font-weight: bold;
      border-bottom: 1.5px solid #000;
      padding-bottom: 4px;
      margin-bottom: 8px;
    }
    .ref-table {
      width: 100%;
      border-collapse: collapse;
    }
    .ref-table th {
      font-size: 10pt;
      font-weight: bold;
      text-align: left;
      padding: 4px 8px;
      background: #f0f0f0;
      border: 1px solid #bbb;
    }
    .ref-table td {
      font-size: 10pt;
      padding: 4px 8px;
      border: 1px solid #bbb;
      height: 22px;
    }

    /* ── Footer ── */
    .footer {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 8px 48px;
      border-top: 1px solid #000;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      font-size: 9pt;
      background: #fff;
    }
    .footer-center { text-align: center; }
    .footer-right  { text-align: right; }

    @media print {
      body { padding: 20px 36px 72px; }
      .footer { bottom: 6px; }
      .paste-area { min-height: 220px; }
      .header-logo { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <div class="header">
    <div class="header-logo-col">
      <img class="header-logo" src="${logoUrl}" alt="Health Hub Logo" />
    </div>
    <div class="header-text">
      <h1>Results</h1>
      <h2>Health Hub Laboratory</h2>
      <p>Phone: (417) 660-4469</p>
      <p>Fax: (417) 281-0061</p>
    </div>
    <div class="header-spacer"></div>
  </div>

  <div class="banner">
    <h3>${tmpl.title}</h3>
  </div>

  <div class="patient-row">
    <div class="patient-box">
      <p>Name: ${patientName}</p>
      <p>Patient ID: ${patientId}</p>
      <p>DOB: ${dob}</p>
      <p>Ordering Clinician:&nbsp; ${clinician}</p>
    </div>
    <div class="collected-box">
      <p><strong>Collected Date:</strong></p>
      <p><strong>Collected Time:</strong></p>
    </div>
  </div>

  <table class="results-table">
    <thead>
      <tr>
        <th rowspan="2" style="width:24%">Test</th>
        <th rowspan="2" style="width:18%">Result</th>
        <th rowspan="2" style="width:12%">Flag</th>
        <th colspan="2" style="width:26%">Reference Range</th>
        <th rowspan="2" style="width:20%">Units</th>
      </tr>
      <tr class="subrow">
        ${refColHeaders}
      </tr>
    </thead>
    <tbody>
      ${tableBodyRows}
    </tbody>
  </table>

  <p class="machine-label">${tmpl.machineName}</p>

  <div class="paste-area">
    <span class="paste-area-hint">Paste ${tmpl.machineName} printout here</span>
  </div>

  <div class="ref-section">
    <h4>Standard Reference Ranges &mdash; ${tmpl.machineName}</h4>
    <table class="ref-table">
      <thead>
        <tr>
          <th style="width:30%">Test</th>
          ${refColHeadersSimple}
          <th style="width:25%">Units</th>
        </tr>
      </thead>
      <tbody>
        ${refTableRows}
      </tbody>
    </table>
  </div>

  <div class="footer">
    <div><p>Health Hub Clinic</p></div>
    <div class="footer-center"><p>healthhubclinic.com</p></div>
    <div class="footer-right">
      <p>(417) 660-4469</p>
      <p style="font-size:8pt">Unauthorized use is strictly prohibited &mdash; contact immediately if this material is received in error.</p>
    </div>
  </div>

</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=900,height=1100');
  if (!printWindow) {
    alert('Pop-up blocked. Please allow pop-ups for this site to print templates.');
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 600);
};
